/**
 * @fileoverview Focus management hook for antd Dropdown + Menu keyboard accessibility.
 *
 * ## Problem
 *
 * antd's Dropdown component renders menus in a portal, but doesn't automatically
 * move focus to the menu when it opens. This breaks keyboard navigation because:
 *
 * 1. rc-menu (which powers antd Menu) has built-in keyboard navigation
 *    (ArrowUp/ArrowDown/Enter), but it only works when the menu container has focus.
 * 2. When a Dropdown opens (via click or Enter on trigger), focus stays on the
 *    trigger button - the menu never receives focus.
 * 3. Without focus on the menu, arrow keys do nothing.
 *
 * ## Solution
 *
 * This hook manages the focus handoff:
 * - When dropdown opens → focus the menu container (activates rc-menu's keyboard nav)
 * - When dropdown closes → return focus to the trigger button
 * - Handle Escape key → close dropdown (rc-menu's Escape is for submenus only)
 *
 * ## What rc-menu handles (once focused)
 *
 * - ArrowDown/ArrowUp: Navigate between menu items
 * - Enter: Activate the focused item
 * - Home/End: Jump to first/last item
 * - Type-ahead: Jump to items starting with typed character
 *
 * ## What this hook handles
 *
 * - Focus transfer: trigger → menu (on open)
 * - Focus transfer: menu → trigger (on close)
 * - Escape key: Close the dropdown entirely
 *
 * ## Timing: Why Double requestAnimationFrame?
 *
 * antd Dropdown renders its menu in a React portal asynchronously. The timing
 * between the visibility state change and the portal appearing in the DOM varies
 * depending on how the dropdown was triggered:
 *
 * - Mouse click: Portal renders quickly, single RAF often sufficient
 * - Keyboard Enter: Additional event handling delays portal rendering
 *
 * Timing options considered:
 *
 * | Method              | Timing           | Result                              |
 * |---------------------|------------------|-------------------------------------|
 * | queueMicrotask      | Before render    | Too early - portal doesn't exist    |
 * | Single RAF          | ~16ms            | Works for click, fails for keyboard |
 * | Double RAF          | ~32ms            | Works reliably for both triggers    |
 * | setTimeout(fn, 50)  | 50ms             | Works but arbitrary/slower          |
 *
 * Double RAF waits for:
 * 1. First RAF: React's commit phase completes (state → DOM mutation)
 * 2. Second RAF: Browser has painted, element is ready for focus
 *
 * ## antd Animation Note
 *
 * antd Dropdown uses a 200ms slide animation (@animation-duration-base in theme).
 * The DOM element exists at animation START, not end - so we can focus immediately.
 * The animation is purely visual and doesn't affect focusability.
 *
 * ## Usage
 *
 * This hook is used internally by BuiMenu when rendered inside a Dropdown.
 * See BuiMenu.stories.tsx "WithFocusManagement" for a complete example.
 */
import { type RefObject, useCallback, useEffect, useRef } from "react";

interface UseMenuFocusOnOpenOptions {
  /**
   * Ref to the trigger element (button that opens the dropdown).
   * Focus returns here when menu closes.
   */
  triggerRef: RefObject<HTMLElement | null>;
  /**
   * Whether the dropdown is currently visible.
   */
  visible: boolean;
  /**
   * Callback when visibility changes. Pass this to Dropdown's onVisibleChange.
   */
  onVisibleChange: (visible: boolean) => void;
  /**
   * Selector to find the menu container. Defaults to antd's menu classes.
   */
  menuSelector?: string;
  /**
   * When `false`, the hook does not move focus to the menu on open, return focus
   * to the trigger on close, or bind the Escape-to-close listener. Use for
   * combobox-style dropdowns whose trigger is a text input the user types into
   * (e.g. BTTimePicker) — stealing focus to the menu would drop keystrokes.
   * @default true
   */
  enabled?: boolean;
  /**
   * When `false`, focus is NOT returned to the trigger when the menu closes, while
   * focus-into-menu on open and Escape-to-close still work. Use when selecting a menu
   * item swaps in its own focus target (e.g. an inline input) that the trigger
   * focus-return would otherwise steal focus from.
   * @default true
   */
  returnFocusOnClose?: boolean;
}

interface UseMenuFocusOnOpenReturn {
  /**
   * Pass this to Dropdown's onVisibleChange prop.
   */
  handleVisibleChange: (visible: boolean) => void;
}

/**
 * Hook to manage focus handoff between dropdown trigger and menu.
 * When dropdown opens, focuses the menu container to activate rc-menu's built-in keyboard navigation.
 * When dropdown closes, returns focus to the trigger.
 * rc-menu handles ArrowUp/ArrowDown/Enter/Escape internally once it has focus.
 * @param options - Hook configuration options.
 * @param options.triggerRef - Ref to the trigger element. Focus returns here when menu closes.
 * @param options.visible - Whether the dropdown is currently visible.
 * @param options.onVisibleChange - Callback when visibility changes.
 * @param options.menuSelector - Selector to find the menu container. Defaults to antd's menu classes.
 * @param options.enabled - When `false`, disables all focus management (open focus, close focus-return, Escape-to-close). Defaults to `true`.
 * @example
 * ```tsx
 * const [visible, setVisible] = useState(false);
 * const triggerRef = useRef<HTMLButtonElement>(null);
 *
 * const { handleVisibleChange } = useMenuFocusOnOpen({
 *   triggerRef,
 *   visible,
 *   onVisibleChange: setVisible,
 * });
 *
 * <Dropdown
 *   visible={visible}
 *   onVisibleChange={handleVisibleChange}
 *   overlay={<BuiMenu>...</BuiMenu>}
 * >
 *   <button ref={triggerRef}>Open</button>
 * </Dropdown>
 * ```
 */
export function useMenuFocusOnOpen({
  triggerRef,
  visible,
  onVisibleChange,
  menuSelector = ".ant-dropdown-menu, .ant-menu",
  enabled = true,
  returnFocusOnClose = true,
}: UseMenuFocusOnOpenOptions): UseMenuFocusOnOpenReturn {
  const previousVisibleRef = useRef(visible);
  const shouldReturnFocusRef = useRef(false);

  // Handle focus when visibility changes
  useEffect(() => {
    if (!enabled) return;

    const wasVisible = previousVisibleRef.current;
    previousVisibleRef.current = visible;

    if (!wasVisible && visible) {
      // Dropdown just opened - focus menu container to activate rc-menu's keyboard nav
      // antd Dropdown renders portal asynchronously with a 200ms slide animation.
      // The DOM element exists at animation start, but we need to wait for React to
      // commit the portal. Double RAF waits ~32ms (2 paint cycles) which handles
      // timing differences between click and keyboard triggers.
      const focusMenu = () => {
        const menu = document.querySelector<HTMLElement>(menuSelector);
        if (menu) {
          menu.focus();
          shouldReturnFocusRef.current = true;
        }
      };

      // Double RAF: first waits for React commit, second ensures DOM is ready
      requestAnimationFrame(() => {
        requestAnimationFrame(focusMenu);
      });
    } else if (wasVisible && !visible && shouldReturnFocusRef.current) {
      if (returnFocusOnClose) {
        triggerRef.current?.focus();
      }
      shouldReturnFocusRef.current = false;
    }
  }, [visible, triggerRef, menuSelector, enabled, returnFocusOnClose]);

  // Handle Escape key to close the dropdown
  // rc-menu's Escape handling is for submenus, not closing the Dropdown itself
  useEffect(() => {
    if (!enabled || !visible) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onVisibleChange(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [enabled, visible, onVisibleChange]);

  const handleVisibleChange = useCallback(
    (newVisible: boolean) => {
      onVisibleChange(newVisible);
    },
    [onVisibleChange],
  );

  return { handleVisibleChange };
}
